/**
 * dormitoryRoomCategoryHelper.js
 * มาตรฐานประเภทห้องพักและกลุ่มเป้าหมาย (Single Source of Truth)
 * ใช้ร่วมกันระหว่างหน้าจัดการหอพักเจ้าหน้าที่ (DormitoryStaffManagement)
 * และหน้าข้อมูลหอพักนักศึกษา (TraineeMasterData)
 */

export const ROOM_CATEGORIES = {
  STAFF_GENERAL: {
    key: "STAFF_GENERAL",
    label: "👨‍💼 ห้องเจ้าหน้าที่ รพ. (ทั่วไป)",
    shortLabel: "เจ้าหน้าที่",
    fullTag: "👨‍💼 จนท. รพ.",
    badgeBg: "#dbeafe",
    badgeColor: "#1e40af",
    badgeBorder: "#93c5fd",
    bsColor: "info",
    targetGroup: "STAFF",
    roomType: "ทั่วไป",
    isFamilyRoom: false,
    defaultMonthlyRate: 0,
    defaultDailyRate: 0,
    description: "สวัสดิการบุคลากร รพ. / ฟรีค่าเช่าห้อง"
  },
  STAFF_FAMILY: {
    key: "STAFF_FAMILY",
    label: "👨‍👩‍👧‍👦 ห้องครอบครัวเจ้าหน้าที่",
    shortLabel: "ครอบครัว",
    fullTag: "👨‍👩‍👧‍👦 ครอบครัว",
    badgeBg: "#ffedd5",
    badgeColor: "#c2410c",
    badgeBorder: "#fdba74",
    bsColor: "warning",
    targetGroup: "STAFF",
    roomType: "ครอบครัว",
    isFamilyRoom: true,
    defaultMonthlyRate: 0,
    defaultDailyRate: 0,
    description: "สวัสดิการบุคลากร รพ. พักอาศัยแบบครอบครัว (ระบุผู้ติดตามได้)"
  },
  STUDENT: {
    key: "STUDENT",
    label: "🎓 ห้องนักศึกษาฝึกงาน",
    shortLabel: "นักศึกษา",
    fullTag: "🎓 นักศึกษา",
    badgeBg: "#dcfce7",
    badgeColor: "#15803d",
    badgeBorder: "#86efac",
    bsColor: "success",
    targetGroup: "STUDENT",
    roomType: "นักศึกษา",
    isFamilyRoom: false,
    defaultMonthlyRate: 500,
    defaultDailyRate: 100,
    description: "นักศึกษาฝึกงาน / สถาบันภายนอก (500 บาท/คน/เดือน หรือ 100 บาท/คน/วัน)"
  },
  DOCTOR: {
    key: "DOCTOR",
    label: "🩺 ห้องแพทย์ Fellow / Elective",
    shortLabel: "แพทย์",
    fullTag: "🩺 แพทย์",
    badgeBg: "#e0e7ff",
    badgeColor: "#4338ca",
    badgeBorder: "#a5b4fc",
    bsColor: "primary",
    targetGroup: "STUDENT",
    roomType: "แพทย์",
    isFamilyRoom: false,
    defaultMonthlyRate: 500,
    defaultDailyRate: 100,
    description: "แพทย์ประจำบ้าน / Elective / Fellow (500 บาท/คน/เดือน หรือ 100 บาท/คน/วัน)"
  },
  ALL: {
    key: "ALL",
    label: "🏢 ห้องส่วนกลาง / อเนกประสงค์",
    shortLabel: "ส่วนกลาง",
    fullTag: "🏢 ส่วนกลาง",
    badgeBg: "#f1f5f9",
    badgeColor: "#334155",
    badgeBorder: "#cbd5e1",
    bsColor: "secondary",
    targetGroup: "ALL",
    roomType: "ทั่วไป",
    isFamilyRoom: false,
    defaultMonthlyRate: 0,
    defaultDailyRate: 0,
    description: "ห้องพักรับรองส่วนกลาง / อเนกประสงค์"
  }
};

export const ROOM_CATEGORY_LIST = Object.values(ROOM_CATEGORIES);

/**
 * วิเคราะห์หา Category Key จาก Object ข้อมูลห้อง
 */
export const getRoomCategoryKey = (room) => {
  if (!room) return "STAFF_GENERAL";

  const rt = String(room.RoomType || room.roomType || room.type || "").trim();
  const tg = String(room.TargetGroup || room.targetGroup || "").toUpperCase();
  const bNo = Number(room.DormitoryID || room.dormitoryID || room.BuildingNo || room.buildingNo || room.buildingId || 0);

  // 1. ครอบครัว (ตึก 3 หรือมี flag ครอบครัว หรือ roomType เป็น ครอบครัว)
  const isFam = Boolean(
    room.IsFamilyRoom ||
    room.isFamilyRoom ||
    rt === "ครอบครัว" ||
    rt === "ห้องครอบครัว" ||
    bNo === 3
  );
  if (isFam) return "STAFF_FAMILY";

  // 2. แพทย์
  if (rt === "แพทย์" || rt.toLowerCase() === "doctor") return "DOCTOR";

  // 3. นักศึกษา
  if (rt === "นักศึกษา" || tg === "STUDENT" || rt.toLowerCase() === "student") return "STUDENT";

  // 4. ส่วนกลาง ALL
  if (tg === "ALL" && rt !== "แพทย์" && rt !== "นักศึกษา") return "ALL";

  // 5. เจ้าหน้าที่ทั่วไป (Default)
  return "STAFF_GENERAL";
};

/**
 * ดึง Object Category รายละเอียดครบถ้วน
 */
export const getRoomCategoryInfo = (room) => {
  const key = getRoomCategoryKey(room);
  return ROOM_CATEGORIES[key] || ROOM_CATEGORIES.STAFF_GENERAL;
};

/**
 * อัปเดตข้อมูลห้องเมื่อมีการเปลี่ยน Category
 */
export const applyRoomCategory = (prevRoom, catKey) => {
  const cat = ROOM_CATEGORIES[catKey] || ROOM_CATEGORIES.STAFF_GENERAL;
  const isFeeApplicable = catKey === "STUDENT" || catKey === "DOCTOR";

  const existingMonthly = Number(prevRoom?.MonthlyRate ?? prevRoom?.monthlyRate ?? prevRoom?.fee ?? 0);
  const existingDaily = Number(prevRoom?.DailyRate ?? prevRoom?.dailyRate ?? prevRoom?.dailyFee ?? 0);

  return {
    ...prevRoom,
    TargetGroup: cat.targetGroup,
    targetGroup: cat.targetGroup,
    RoomType: cat.roomType,
    roomType: cat.roomType,
    type: cat.roomType,
    IsFamilyRoom: cat.isFamilyRoom,
    isFamilyRoom: cat.isFamilyRoom,
    MonthlyRate: isFeeApplicable ? (existingMonthly > 0 ? existingMonthly : cat.defaultMonthlyRate) : 0,
    monthlyRate: isFeeApplicable ? (existingMonthly > 0 ? existingMonthly : cat.defaultMonthlyRate) : 0,
    fee: isFeeApplicable ? (existingMonthly > 0 ? existingMonthly : cat.defaultMonthlyRate) : 0,
    DailyRate: isFeeApplicable ? (existingDaily > 0 ? existingDaily : cat.defaultDailyRate) : 0,
    dailyRate: isFeeApplicable ? (existingDaily > 0 ? existingDaily : cat.defaultDailyRate) : 0,
    dailyFee: isFeeApplicable ? (existingDaily > 0 ? existingDaily : cat.defaultDailyRate) : 0
  };
};

